import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
  X,
  Group,
  ArrowUpDown,
  Lock,
} from "lucide-react";
import { DatasetOrderBy } from "@forgepoint/shared";
import { useDatasetExplorerStore } from "../datasetExplorerStore";

/**
 * GroupBy and OrderBy configuration panel.
 * GroupBy is only visible when aggregate functions are in use.
 */
export default function GroupByOrderBy() {
  const selectedFields = useDatasetExplorerStore((s) => s.selectedFields);
  const groupBy = useDatasetExplorerStore((s) => s.groupBy);
  const orderBy = useDatasetExplorerStore((s) => s.orderBy);
  const setGroupBy = useDatasetExplorerStore((s) => s.setGroupBy);
  const setOrderBy = useDatasetExplorerStore((s) => s.setOrderBy);

  const hasAggregates = selectedFields.some((f) => f.aggregate);
  const nonAggregatedFields = selectedFields.filter((f) => !f.aggregate);
  const autoIncludedFields = new Set(nonAggregatedFields.map((f) => f.field));

  const orderableFields = selectedFields.map((f) => {
    if (f.alias) {
      return f.alias;
    }
    if (f.aggregate) {
      return f.field.replace(".", "_");
    }
    return f.field;
  });

  return (
    <div className="space-y-4">
      {hasAggregates && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Group className="h-3 w-3" />
            Group By
          </Label>
          {nonAggregatedFields.length > 0 ? (
            <div className="space-y-1">
              <div className="flex flex-wrap gap-1">
                {nonAggregatedFields.map((f) => (
                  <Badge
                    key={f.field}
                    variant="default"
                    className="text-xs cursor-default"
                  >
                    <Lock className="h-2.5 w-2.5 mr-1 opacity-60" />
                    {f.field.split(".")[1]}
                  </Badge>
                ))}
                {groupBy
                  .filter((g) => !autoIncludedFields.has(g))
                  .map((g) => (
                    <Badge
                      key={g}
                      variant="default"
                      className="cursor-pointer text-xs transition-colors"
                      onClick={() =>
                        setGroupBy(groupBy.filter((gb) => gb !== g))
                      }
                    >
                      {g.split(".")[1]}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
              </div>
              <p className="text-[10px] text-muted-foreground/60">
                Non-aggregated fields are automatically grouped.
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/70">
              All selected fields are aggregated — no grouping needed.
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <ArrowUpDown className="h-3 w-3" />
          Order By
        </Label>
        <div className="space-y-1">
          {orderBy.map((entry, index) => (
            <div key={index} className="flex items-center gap-1">
              <Select
                value={entry.field}
                onValueChange={(field) => {
                  const updated: DatasetOrderBy[] = orderBy.map((o, i) =>
                    i === index ? { ...o, field } : o,
                  );
                  setOrderBy(updated);
                }}
              >
                <SelectTrigger className="h-7 text-xs flex-1 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {orderableFields.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => {
                  const updated: DatasetOrderBy[] = orderBy.map((o, i) =>
                    i === index
                      ? {
                          ...o,
                          direction: o.direction === "asc" ? "desc" : "asc",
                        }
                      : o,
                  );
                  setOrderBy(updated);
                }}
              >
                {entry.direction === "asc" ? (
                  <ArrowUp className="h-3.5 w-3.5" />
                ) : (
                  <ArrowDown className="h-3.5 w-3.5" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  setOrderBy(orderBy.filter((_, i) => i !== index))
                }
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}

          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            onClick={() =>
              setOrderBy([
                ...orderBy,
                {
                  field: orderableFields[0] ?? "",
                  direction: "asc",
                },
              ])
            }
            disabled={orderableFields.length === 0}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Sort
          </Button>
        </div>
      </div>
    </div>
  );
}
